import type { SearchMember, WhoSelection } from "@ui/components/SearchBar";
import { useCallback, useMemo, useState } from "react";

const EMPTY_WHO: WhoSelection = { memberIds: [] };

// Programmes are age-banded for children, so only children constrain the search.
// Adults (and members with an unknown age) impose no age filter — nothing is
// serviced "for" an adult's age. Matches the 18+ account age gate.
const ADULT_AGE_YEARS = 18;

export interface UseSearchBarWhoReturn {
  whoProps: {
    value: WhoSelection;
    onChange: (value: WhoSelection) => void;
  };
  /** Ages of the selected members (members without a known age drop out), or null when nothing is selected. */
  whoAges: number[] | null;
  resetWho: () => void;
}

/**
 * Holds the SearchBar "Who" selection for /programs and derives the ages the
 * search request filters on. Mirrors `useSearchBarWhen` — pure client state. The
 * member list is needed to resolve selected ids to ages; a null result means the
 * server-side age filter is a no-op.
 */
export function useSearchBarWho(members: SearchMember[], initial?: WhoSelection): UseSearchBarWhoReturn {
  const [value, setValue] = useState<WhoSelection>(initial ?? EMPTY_WHO);

  // Per-member semantics over CHILDREN only: a programme must suit every selected
  // child (age < 18) whose age we know. Adults and members with an unknown age
  // impose no constraint — they drop out here on purpose (do NOT treat them as age
  // 0/∞). When no selected member is an age-known child, the filter is a no-op (null).
  const whoAges = useMemo(() => {
    if (value.memberIds.length === 0) return null;
    const ageById = new Map(members.map((member) => [member.id, member.age]));
    const ages = value.memberIds
      .map((id) => ageById.get(id))
      .filter((age): age is number => typeof age === "number" && age < ADULT_AGE_YEARS);
    return ages.length > 0 ? ages : null;
  }, [value.memberIds, members]);

  const resetWho = useCallback(() => setValue(EMPTY_WHO), []);

  return { whoProps: { value, onChange: setValue }, whoAges, resetWho };
}
